import {
  DataGridPremium,
  GRID_REORDER_COL_DEF,
  GridRenderCellParams,
  GridRowModel,
  GridRowOrderChangeParams,
} from '@mui/x-data-grid-premium';
import { FC, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWhiteCache } from '../../../../lib/white-cache';
import { AppDispatch, RootState } from '../../../../store';
import { EnableCell } from './enable-cell';
import { setCurrentService, setOpenForm, setServices } from '../../store/root';
import { Badge, Checkbox, IconButton, Stack } from '@mui/material';
import { nanoid } from 'nanoid';
import { DynamicIconConverter } from '../../../../util/dynamic-icon-converter';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { updateServicePositions } from '../../api/update-service-positions';
import { useQueryClient } from '@tanstack/react-query';

interface ServicesGridInterface {
  search: string;
  isActive: boolean;
}

export const ServicesGrid: FC<ServicesGridInterface> = memo(
  ({ search, isActive }) => {
    const { services, serviceLoad } = useSelector(
      (state: RootState) => state.serviceRoot
    );
    const dispatch = useDispatch<AppDispatch>();
    const { isBluBookingActive } = getWhiteCache();
    const queryClient = useQueryClient();

    const getValues = useCallback(() => {
      if (services !== undefined)
        return Object.values(services)
          .sort((a, b) => a.position - b.position)
          .filter((item: any) => {
            if (isActive) {
              return item.isActive === true;
            } else {
              return item;
            }
          })
          .filter((value) => {
            const reg = new RegExp(search, 'i');
            return reg.test(value.name);
          })
          .map((item) => ({ ...item, __reorder__: item.name }));
      return [];
    }, [isActive, search, services]);

    const updateRows = useCallback(
      async (initialIndex: number, newIndex: number) => {
        const rowsClone = Object.values(services);
        const row = rowsClone.splice(initialIndex, 1)[0];
        rowsClone.splice(newIndex, 0, row);

        const orderedServices = rowsClone.map((item, index) => ({
          serviceId: item.id,
          position: index + 1,
        }));

        const result = await updateServicePositions(orderedServices);
        if (result) {
          const mappedServices = {};
          rowsClone.forEach((item, index) => {
            mappedServices[item.id] = {
              ...item,
              position: index + 1,
            };
          });

          dispatch(setServices(mappedServices));
          queryClient.invalidateQueries({ queryKey: ['details', 'service'] });
          queryClient.invalidateQueries({
            queryKey: ['all-filters', 'services'],
          });
          queryClient.invalidateQueries({
            queryKey: ['ui', 'services'],
          });
        }
      },
      [dispatch, queryClient, services]
    );

    const handleRowOrderChange = useCallback(
      (params: GridRowOrderChangeParams) => {
        updateRows(params.oldIndex, params.targetIndex);
      },
      [updateRows]
    );

    const handleRowMove = useCallback(
      (id: string, previousPosition: number, direction: 'up' | 'down') => {
        let newPosition = previousPosition - 1;
        if (direction === 'down') newPosition = previousPosition + 1;
        updateRows(previousPosition - 1, newPosition - 1);
      },
      [updateRows]
    );

    const Columns: any[] = [
      {
        field: 'id',
        hide: true,
      },
      {
        field: 'isActive',
        headerName: 'Attivo',
        width: 75,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams) => {
          return <Checkbox checked={!!params.value} disabled />;
        },
      },
      {
        field: 'icons',
        headerName: 'Icone',
        width: 110,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Stack flex="1" direction="row" gap={1} justifyContent="center">
              {(params.value ?? []).map((item: any) => (
                <DynamicIconConverter name={item} />
              ))}
            </Stack>
          );
        },
      },
      {
        field: 'color',
        headerName: 'Colore',
        width: 70,
        editable: false,
        disableColumnMenu: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams) => {
          // eslint-disable-next-line no-extra-boolean-cast
          return !!params.value ? (
            <Badge
              key={params.value + 'color'}
              sx={{
                borderRadius: '50%',
                backgroundColor: `${params.value}95`,
                width: 30,
                height: 30,
                border: `thin solid ${params.value}`,
              }}
              overlap="circular"
            />
          ) : (
            <Badge
              key={nanoid()}
              sx={{
                borderRadius: '50%',
                backgroundColor: 'white',
                width: 30,
                height: 30,
                border: `thin solid var(--divider-color)`,
              }}
              overlap="circular"
            />
          );
        },
      },
      {
        field: 'name',
        headerName: 'Servizio',
        flex: 1,
        editable: false,
        minWidth: 70,
      },
      {
        field: 'smallDescription',
        headerName: 'Descrizione',
        flex: 1,
        editable: false,
        minWidth: 100,
      },
      // {
      //   field: 'description',
      //   headerName: 'Descrizione Blu Booking',
      //   flex: 1.5,
      //   editable: false,
      //   hide: !isBluBookingActive,
      //   minWidth: 170,
      // },
      {
        field: 'durationValue',
        headerName: 'Durata',
        type: 'number',
        align: 'center',
        width: 70,
        editable: false,
        sortable: false,
        headerAlign: 'center',
      },
      {
        field: 'isOnlineBooking',
        headerName: 'Prenotazione online',
        align: 'center',
        type: 'boolean',
        width: 180,
        editable: false,
        sortable: false,
        hide: !isBluBookingActive,
        renderCell: EnableCell,
      },
      {
        field: 'hasAutoConfirmation',
        headerName: 'Auto conferma',
        align: 'center',
        type: 'boolean',
        width: 130,
        editable: false,
        sortable: false,
        hide: !isBluBookingActive,
        renderCell: EnableCell,
      },
      {
        field: 'hasStudio',
        headerName: 'Studio necessario',
        align: 'center',
        type: 'boolean',
        width: 170,
        editable: false,
        sortable: false,
        renderCell: EnableCell,
      },
      {
        field: 'ordina',
        headerName: 'Ordina',
        width: 95,
        disableColumnMenu: true,
        sortable: false,
        headerAlign: 'center',
        align: 'center',
        renderCell: (params: GridRenderCellParams) => {
          const values = getValues();
          // console.log('params', params['row'].position, services.length);
          return (
            <Stack direction={'row'}>
              <IconButton
                color={'primary'}
                disabled={params['row'].position === 1}
                aria-label="up"
                onClick={() =>
                  handleRowMove(params.row.id, params['row'].position, 'up')
                }
              >
                <KeyboardArrowUpIcon />
              </IconButton>
              <IconButton
                color={'primary'}
                disabled={params['row'].position === values.length}
                aria-label="down"
                onClick={() =>
                  handleRowMove(params.row.id, params['row'].position, 'down')
                }
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        ...GRID_REORDER_COL_DEF,
        field: '__reorder__',
        headerName: '',
        sortable: false,
        filterable: false,
        width: 50,
      },
    ];

    return (
      <DataGridPremium
        columns={Columns as any}
        rows={getValues()}
        loading={serviceLoad}
        sx={{
          '& .MuiDataGrid-cell': {
            outline: 'none!important',
          },
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        rowBuffer={50}
        rowThreshold={15}
        density="compact"
        disableColumnMenu
        getEstimatedRowHeight={() => 300}
        getRowHeight={() => 'auto'}
        editMode="cell"
        onProcessRowUpdateError={(error) => console.log(error)}
        experimentalFeatures={{ newEditingApi: true }}
        onRowClick={(event: any) => {
          dispatch(setCurrentService(event.id));
        }}
        onRowDoubleClick={() => dispatch(setOpenForm('info'))}
        rowReordering
        onRowOrderChange={handleRowOrderChange}
        components={{
          Footer: () => <></>,
        }}
      />
    );
  }
);
